import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ThriveModal from "../../common/ThriveModal";
import { axiosInstance } from "../../../utils/networking";
import {
  OutcomeRecordKind,
  OutcomeRecordType,
  defaultOutcome,
} from "../../../interfaces";
import {
  DOLLAR_OUTCOME_TYPES,
  OUTCOME_TYPE_OPTIONS,
  outcomeHasValue,
  outcomeTypeLabels,
} from "./OutcomeEditModal";

type OutcomeCaptureModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  caseId: string;
  firmId: string;
  onCloseCase: () => void;
};

// Case-close intercept — the highest-yield outcome capture point. Both paths
// ("Skip & Close" and "Save & Close") end by calling the existing closeCase;
// the close flow itself is unchanged. Saved outcomes carry source "case_close".
const OutcomeCaptureModal: React.FC<OutcomeCaptureModalProps> = ({
  open,
  setOpen,
  caseId,
  firmId,
  onCloseCase,
}) => {
  const [drafts, setDrafts] = useState<OutcomeRecordType[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDrafts([]);
  }, [open]);

  const newDraft = (): OutcomeRecordType => ({
    ...defaultOutcome,
    case_id: caseId,
    firm_id: firmId,
    date: new Date(),
    source: "case_close",
  });

  const updateDraft = (index: number, patch: Partial<OutcomeRecordType>) => {
    setDrafts((prev) =>
      prev.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
    );
  };

  const handleSkipAndClose = () => {
    setOpen(false);
    onCloseCase();
  };

  const handleSaveAndClose = () => {
    setSaving(true);
    Promise.all(
      drafts.map((draft) =>
        axiosInstance.post(`/outcomes`, {
          ...draft,
          value: outcomeHasValue(draft.type) ? draft.value : null,
        }),
      ),
    )
      .then(() => {
        setSaving(false);
        setOpen(false);
        onCloseCase();
      })
      .catch(() => setSaving(false));
  };

  return (
    <ThriveModal
      open={open}
      setOpen={setOpen}
      closeByClick={true}
      width={720}
      height={520}
      fullScreenOnPhone
      contentComponent={
        <Box sx={{ padding: { xs: "16px", sm: "24px" }, display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography variant="h6">Before closing — record any outcomes for this client</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", marginBottom: "12px" }}>
            Businesses started, jobs created, capital accessed… These power the
            impact numbers leadership reports to funders.
          </Typography>
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {drafts.map((draft, index) => {
              const showValue = outcomeHasValue(draft.type);
              const isDollar = DOLLAR_OUTCOME_TYPES.includes(draft.type);
              // Below md the row wraps: on phones the type takes a line, value
              // and date share the next, then the note and the remove button.
              return (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{
                    alignItems: "center",
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Select
                    size="small"
                    sx={{
                      minWidth: 170,
                      flexBasis: { xs: "100%", sm: "auto" },
                    }}
                    value={draft.type}
                    onChange={(e) =>
                      updateDraft(index, {
                        type: e.target.value as OutcomeRecordKind,
                        value: null,
                      })
                    }
                  >
                    {OUTCOME_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        {outcomeTypeLabels[type]}
                      </MenuItem>
                    ))}
                  </Select>
                  {showValue && (
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 140, flexGrow: { xs: 1, sm: 0 } }}
                      value={draft.value ?? ""}
                      onChange={(e) =>
                        updateDraft(index, {
                          value:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                      InputProps={{
                        startAdornment: isDollar ? (
                          <InputAdornment position="start">$</InputAdornment>
                        ) : undefined,
                        endAdornment: !isDollar ? (
                          <InputAdornment position="end">jobs</InputAdornment>
                        ) : undefined,
                      }}
                    />
                  )}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={dayjs(draft.date)}
                      onChange={(value: any) =>
                        updateDraft(index, {
                          date: value ? value.toDate() : new Date(),
                        })
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { width: 160, flexGrow: { xs: 1, sm: 0 } },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <TextField
                    size="small"
                    placeholder="Note"
                    sx={{ flexGrow: 1 }}
                    value={draft.note}
                    onChange={(e) => updateDraft(index, { note: e.target.value })}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setDrafts((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setDrafts((prev) => [...prev, newDraft()])}
            >
              {drafts.length === 0 ? "Add an outcome" : "Add another outcome"}
            </Button>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              justifyContent: "flex-end",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              marginTop: "12px",
            }}
          >
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outlined" color="warning" onClick={handleSkipAndClose}>
              Skip &amp; Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAndClose}
              disabled={saving || drafts.length === 0}
            >
              Save &amp; Close
            </Button>
          </Stack>
        </Box>
      }
    />
  );
};

export default OutcomeCaptureModal;
